import {
  CreateDocumentUploadRequestSchema,
  DocumentTargetTypeSchema,
  IdSchema,
} from '@nlc/contracts';
import { z } from 'zod';

/** A local draft lives for a day; a capture older than that is stale, not evidence. */
export const UPLOAD_TTL_MS = 24 * 60 * 60 * 1000;
const DATABASE_PREFIX = 'nlc-uploads-';
const STORE_NAME = 'uploads';
const DATABASE_VERSION = 1;

/** Limits come from the upload contract so the queue never holds a file the server refuses. */
const uploadLimits = CreateDocumentUploadRequestSchema.shape;
export const MAX_UPLOAD_BYTES = uploadLimits.sizeBytes.maxValue ?? 25 * 1024 * 1024;
export const MAX_FILE_NAME_LENGTH = uploadLimits.fileName.maxLength ?? 255;
const MAX_MIME_TYPE_LENGTH = uploadLimits.mimeType.maxLength ?? 150;

export const UploadPhaseSchema = z.enum([
  'queued',
  'requesting',
  'uploading',
  'completing',
  'failed',
]);
export type UploadPhase = z.infer<typeof UploadPhaseSchema>;

/**
 * A pending capture: the minimum needed to finish the three-step upload later. Rows read back
 * from IndexedDB are validated against this so a corrupt or foreign row is dropped, not trusted.
 */
export const QueuedUploadSchema = z.object({
  id: z.string().min(1),
  targetType: DocumentTargetTypeSchema,
  targetId: z.string().min(1),
  fileName: z.string().min(1),
  mimeType: z.string().min(1),
  blob: z.instanceof(Blob),
  createdAt: z.number(),
  expiresAt: z.number(),
  attempts: z.number().int().nonnegative(),
  lastError: z.string().nullable(),
  phase: UploadPhaseSchema,
  documentId: IdSchema.optional(),
  uploadUrl: z.url().optional(),
  requiredHeaders: z.record(z.string(), z.string()).optional(),
});
export type QueuedUpload = z.infer<typeof QueuedUploadSchema>;
export type NewUpload = Pick<
  QueuedUpload,
  'targetType' | 'targetId' | 'fileName' | 'mimeType' | 'blob'
>;
export type UploadPatch = Partial<Omit<QueuedUpload, 'id'>>;

export type UploadQueueScope = { organizationId: string; membershipId: string };
/** `unknown` until the store has been opened, then where entries actually live. */
export type UploadStorage = 'unknown' | 'indexeddb' | 'memory';

/** The persistence the queue sits on. Reads return raw rows; the queue validates them. */
export type QueueStore = {
  getAll: () => Promise<unknown[]>;
  get: (id: string) => Promise<unknown>;
  put: (entry: QueuedUpload) => Promise<void>;
  delete: (id: string) => Promise<void>;
  clear: () => Promise<void>;
  close: () => void;
};

export type UploadQueueOptions = {
  openStore?: (name: string) => Promise<QueueStore>;
  onChange?: () => void;
  now?: () => number;
  newId?: () => string;
};

/** What the store holds for one entry right now, read past this tab's memory copy. */
export type RefreshedUpload = {
  entry: QueuedUpload | undefined;
  /** The stored row says another tab is mid-upload; only meaningful when nothing serializes runs. */
  busyElsewhere: boolean;
};

export type UploadQueue = {
  readonly scope: UploadQueueScope;
  /** Resolves once persisted entries have been loaded; every operation waits on it. */
  ready: () => Promise<void>;
  storage: () => UploadStorage;
  /** Synchronous view for React; the array identity changes only when the queue changes. */
  snapshot: () => readonly QueuedUpload[];
  /** Rejects with a plain message when the contract would refuse the file; nothing is stored. */
  enqueue: (input: NewUpload) => Promise<QueuedUpload>;
  list: () => Promise<QueuedUpload[]>;
  get: (id: string) => Promise<QueuedUpload | undefined>;
  update: (id: string, patch: UploadPatch) => Promise<QueuedUpload | undefined>;
  remove: (id: string) => Promise<void>;
  /** Re-reads one entry from the store so work done in another tab is seen before running it. */
  refresh: (id: string) => Promise<RefreshedUpload>;
  /** Re-reads every entry from the store, keeping writes this tab has not finished yet. */
  resync: () => Promise<void>;
  purgeExpired: () => Promise<number>;
  purgeAll: () => Promise<void>;
};

/** Thrown by `enqueue` for a file the upload contract would reject; the message is for users. */
export class UploadRejectedError extends Error {}

/** Drafts are scoped to one member of one organization; the database name carries both. */
export function databaseName(scope: UploadQueueScope): string {
  return `${DATABASE_PREFIX}${scope.organizationId}-${scope.membershipId}`;
}

export function isExpired(entry: Pick<QueuedUpload, 'expiresAt'>, now: number): boolean {
  return entry.expiresAt <= now;
}

const IN_PROGRESS: readonly UploadPhase[] = ['requesting', 'uploading', 'completing'];

export function isInProgress(entry: Pick<QueuedUpload, 'phase'>): boolean {
  return IN_PROGRESS.includes(entry.phase);
}

/** Nothing is in flight after a reload, so a phase left mid-upload goes back to the queue. */
export function normalizeAfterReload(entry: QueuedUpload): QueuedUpload {
  return isInProgress(entry) ? { ...entry, phase: 'queued' } : entry;
}

/** Step 1 (create the document) is skipped while an instruction is stored: the PUT resumes. */
export function hasUploadInstruction<T extends Pick<QueuedUpload, 'documentId' | 'uploadUrl'>>(
  entry: T,
): entry is T & { documentId: string; uploadUrl: string } {
  return entry.documentId !== undefined && entry.uploadUrl !== undefined;
}

/**
 * The reason the upload contract would refuse this file, in words the user can act on, or
 * `null` when it can be sent. Checked before anything is stored so the queue never carries a
 * draft that can never succeed.
 */
export function uploadRejection(
  input: Pick<NewUpload, 'fileName' | 'mimeType' | 'blob'>,
): string | null {
  if (input.blob.size === 0) return 'This file is empty and cannot be uploaded.';
  if (input.blob.size > MAX_UPLOAD_BYTES) {
    return `Files must be ${String(Math.floor(MAX_UPLOAD_BYTES / (1024 * 1024)))} MB or smaller.`;
  }
  if (input.fileName.trim().length === 0) return 'The file needs a name.';
  if (input.fileName.length > MAX_FILE_NAME_LENGTH) {
    return `The file name is too long (${String(MAX_FILE_NAME_LENGTH)} characters at most).`;
  }
  if (input.mimeType.trim().length === 0 || input.mimeType.length > MAX_MIME_TYPE_LENGTH) {
    return 'The file type is not recognized.';
  }
  return null;
}

function indexedDbFactory(): IDBFactory | undefined {
  return 'indexedDB' in globalThis ? globalThis.indexedDB : undefined;
}

function request<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    try {
      const transaction = db.transaction(STORE_NAME, mode);
      const pending = operation(transaction.objectStore(STORE_NAME));
      pending.onsuccess = () => resolve(pending.result);
      pending.onerror = () => reject(pending.error ?? new Error('IndexedDB request failed'));
      transaction.onabort = () =>
        reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
    } catch (error: unknown) {
      reject(error instanceof Error ? error : new Error('IndexedDB is unavailable'));
    }
  });
}

/** Opens (or creates) the scoped database. Rejects when IndexedDB is missing or blocked. */
export function openIndexedDbStore(name: string): Promise<QueueStore> {
  return new Promise<QueueStore>((resolve, reject) => {
    const factory = indexedDbFactory();
    if (!factory) {
      reject(new Error('IndexedDB is not available'));
      return;
    }
    let opening: IDBOpenDBRequest;
    try {
      opening = factory.open(name, DATABASE_VERSION);
    } catch (error: unknown) {
      reject(error instanceof Error ? error : new Error('IndexedDB is blocked'));
      return;
    }
    opening.onupgradeneeded = () => {
      const db = opening.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    opening.onerror = () => reject(opening.error ?? new Error('IndexedDB could not be opened'));
    opening.onblocked = () => reject(new Error('IndexedDB is blocked by another tab'));
    opening.onsuccess = () => {
      const db = opening.result;
      // A sign-out in another tab deletes the database; closing lets that go through.
      db.onversionchange = () => db.close();
      resolve({
        getAll: () => request<unknown[]>(db, 'readonly', (store) => store.getAll()),
        get: (id) => request<unknown>(db, 'readonly', (store) => store.get(id)),
        put: (entry) => request(db, 'readwrite', (store) => store.put(entry)).then(() => undefined),
        delete: (id) => request(db, 'readwrite', (store) => store.delete(id)).then(() => undefined),
        clear: () => request(db, 'readwrite', (store) => store.clear()).then(() => undefined),
        close: () => db.close(),
      });
    };
  });
}

/** Removes every upload database on this device, whoever they belonged to. Never throws. */
export async function deleteUploadDatabases(): Promise<void> {
  const factory = indexedDbFactory();
  if (!factory) return;
  const listDatabases = (factory as { databases?: IDBFactory['databases'] }).databases;
  if (listDatabases === undefined) return;
  let names: string[];
  try {
    const found = await listDatabases.call(factory);
    names = found
      .map((info) => info.name)
      .filter((name): name is string => name?.startsWith(DATABASE_PREFIX) ?? false);
  } catch {
    return;
  }
  await Promise.all(
    names.map(
      (name) =>
        new Promise<void>((resolve) => {
          try {
            const deletion = factory.deleteDatabase(name);
            deletion.onsuccess = () => resolve();
            deletion.onerror = () => resolve();
            deletion.onblocked = () => resolve();
          } catch {
            resolve();
          }
        }),
    ),
  );
}

/**
 * Pending captures for one member of one organization. Entries are always held in memory and
 * written through to IndexedDB; if the store is missing, blocked, or fails mid-session, the
 * queue keeps working in memory only and reports `storage() === 'memory'` so the UI can say a
 * reload will lose them. Other tabs share the store, not the memory: `refresh` and `resync`
 * bring in what they did.
 */
export function createUploadQueue(
  scope: UploadQueueScope,
  options: UploadQueueOptions = {},
): UploadQueue {
  const openStore = options.openStore ?? openIndexedDbStore;
  const now = options.now ?? Date.now;
  const newId = options.newId ?? (() => crypto.randomUUID());
  const entries = new Map<string, QueuedUpload>();
  /** Ids with a `put` still on its way to the store; a resync must not drop them. */
  const writing = new Map<string, number>();
  let cached: readonly QueuedUpload[] | null = null;
  let store: QueueStore | null = null;
  let storage: UploadStorage = 'unknown';
  let hydration: Promise<void> | null = null;

  const changed = (): void => {
    cached = null;
    options.onChange?.();
  };

  const snapshot = (): readonly QueuedUpload[] => {
    cached ??= [...entries.values()].sort((a, b) => a.createdAt - b.createdAt);
    return cached;
  };

  const degrade = (): void => {
    try {
      store?.close();
    } catch {
      // Closing a broken connection may itself throw; memory mode is the outcome either way.
    }
    store = null;
    storage = 'memory';
    changed();
  };

  /** Persistence failures never fail the upload; the entry simply stops surviving a reload. */
  const persist = async (operation: (target: QueueStore) => Promise<void>): Promise<void> => {
    if (!store) return;
    try {
      await operation(store);
    } catch {
      degrade();
    }
  };

  const write = async (entry: QueuedUpload): Promise<void> => {
    writing.set(entry.id, (writing.get(entry.id) ?? 0) + 1);
    try {
      await persist((target) => target.put(entry));
    } finally {
      const left = (writing.get(entry.id) ?? 1) - 1;
      if (left <= 0) writing.delete(entry.id);
      else writing.set(entry.id, left);
    }
  };

  const readRows = async (): Promise<unknown[] | undefined> => {
    if (!store) return undefined;
    try {
      return await store.getAll();
    } catch {
      degrade();
      return undefined;
    }
  };

  const hydrate = async (): Promise<void> => {
    let opened: QueueStore;
    let rows: unknown[];
    try {
      opened = await openStore(databaseName(scope));
    } catch {
      storage = 'memory';
      changed();
      return;
    }
    try {
      rows = await opened.getAll();
    } catch {
      try {
        opened.close();
      } catch {
        // Nothing more to release.
      }
      storage = 'memory';
      changed();
      return;
    }
    for (const row of rows) {
      const parsed = QueuedUploadSchema.safeParse(row);
      if (!parsed.success) continue;
      const entry = normalizeAfterReload(parsed.data);
      if (!entries.has(entry.id)) entries.set(entry.id, entry);
    }
    store = opened;
    storage = 'indexeddb';
    changed();
  };

  const ready = (): Promise<void> => {
    hydration ??= hydrate();
    return hydration;
  };

  const purgeExpired = async (): Promise<number> => {
    await ready();
    const time = now();
    const expired = snapshot().filter((entry) => isExpired(entry, time));
    if (expired.length === 0) return 0;
    for (const entry of expired) entries.delete(entry.id);
    changed();
    for (const entry of expired) await persist((target) => target.delete(entry.id));
    return expired.length;
  };

  return {
    scope,
    ready,
    storage: () => storage,
    snapshot,
    enqueue: async (input) => {
      const rejection = uploadRejection(input);
      if (rejection !== null) throw new UploadRejectedError(rejection);
      await ready();
      const createdAt = now();
      const entry: QueuedUpload = {
        id: newId(),
        targetType: input.targetType,
        targetId: input.targetId,
        fileName: input.fileName,
        mimeType: input.mimeType,
        blob: input.blob,
        createdAt,
        expiresAt: createdAt + UPLOAD_TTL_MS,
        attempts: 0,
        lastError: null,
        phase: 'queued',
      };
      entries.set(entry.id, entry);
      changed();
      await write(entry);
      return entry;
    },
    list: async () => {
      await purgeExpired();
      return [...snapshot()];
    },
    get: async (id) => {
      await ready();
      return entries.get(id);
    },
    update: async (id, patch) => {
      await ready();
      const current = entries.get(id);
      if (!current) return undefined;
      const next: QueuedUpload = { ...current, ...patch, id };
      entries.set(id, next);
      changed();
      await write(next);
      return next;
    },
    remove: async (id) => {
      await ready();
      if (!entries.delete(id)) return;
      changed();
      await persist((target) => target.delete(id));
    },
    refresh: async (id) => {
      await ready();
      // Memory only, or a write still on its way: this tab's copy is the freshest there is.
      if (!store || writing.has(id)) return { entry: entries.get(id), busyElsewhere: false };
      let row: unknown;
      try {
        row = await store.get(id);
      } catch {
        degrade();
        return { entry: entries.get(id), busyElsewhere: false };
      }
      const parsed = QueuedUploadSchema.safeParse(row);
      if (!parsed.success) {
        // Finished or discarded in another tab, or a row nobody can use: it is not ours to run.
        if (entries.delete(id)) changed();
        if (row !== undefined) await persist((target) => target.delete(id));
        return { entry: undefined, busyElsewhere: false };
      }
      const entry = normalizeAfterReload(parsed.data);
      entries.set(id, entry);
      changed();
      return { entry, busyElsewhere: isInProgress(parsed.data) };
    },
    resync: async () => {
      await ready();
      const rows = await readRows();
      if (rows === undefined) return;
      const seen = new Set<string>();
      for (const row of rows) {
        const parsed = QueuedUploadSchema.safeParse(row);
        if (!parsed.success) continue;
        seen.add(parsed.data.id);
        if (!writing.has(parsed.data.id)) {
          entries.set(parsed.data.id, normalizeAfterReload(parsed.data));
        }
      }
      for (const id of [...entries.keys()]) {
        if (!seen.has(id) && !writing.has(id)) entries.delete(id);
      }
      changed();
    },
    purgeExpired,
    purgeAll: async () => {
      await ready();
      entries.clear();
      changed();
      await persist((target) => target.clear());
      try {
        store?.close();
      } catch {
        // Already closed or broken; either way nothing is left open.
      }
      store = null;
    },
  };
}
